import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, Package, IndianRupee, Loader2 } from "lucide-react";

/**
 * Compact daily sales chart shown on the Owner Overview. Fetches
 * /marketplace/analytics/daily and renders a lightweight bar chart with
 * pure SVG — no external chart library to keep bundle size small.
 */
export default function SalesChart({ days = 14 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/marketplace/analytics/daily?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [days]);

  const { max, bars, totals } = useMemo(() => {
    if (!data) return { max: 0, bars: [], totals: null };
    const max = Math.max(1, ...data.days.map((d) => d.revenue));
    return { max, bars: data.days, totals: data.totals };
  }, [data]);

  if (err) return null; // Silent — this widget is best-effort
  if (!data) {
    return (
      <Card className="border-slate-200 shadow-none">
        <CardContent className="p-4 h-40 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 shadow-none" data-testid="overview-sales-chart">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
              Last {days} days sales
            </div>
            <div className="mt-0.5 flex items-baseline gap-3">
              <div className="flex items-center gap-1 text-lg font-bold">
                <IndianRupee className="w-4 h-4" />
                {totals.revenue.toLocaleString("en-IN")}
              </div>
              <div className="flex items-center gap-1 text-sm text-slate-500">
                <Package className="w-3.5 h-3.5" /> {totals.orders} orders
              </div>
            </div>
          </div>
          <TrendingUp className="w-5 h-5 text-emerald-500" />
        </div>

        {/* Bars */}
        <div className="flex items-end gap-1 h-24" data-testid="overview-sales-bars">
          {bars.map((b) => {
            const h = Math.max(2, Math.round((b.revenue / max) * 90));
            return (
              <div
                key={b.date}
                className="flex-1 flex flex-col items-center gap-0.5"
                title={`${b.date}: ₹${b.revenue.toLocaleString("en-IN")} · ${b.orders} orders`}
              >
                <div
                  className={"w-full rounded-t transition-all " + (b.revenue > 0 ? "bg-[#0055FF]" : "bg-slate-200")}
                  style={{ height: `${h}px` }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-1 mono text-[9px] text-slate-400">
          <span>{bars[0]?.date.slice(5)}</span>
          <span>{bars[bars.length - 1]?.date.slice(5)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
